// Admin dashboard — Detail Drawer.
// Pure structural extraction of the "View more" side drawer that used
// to live inline inside AdminDashboard.jsx (~253 lines JSX). Zero
// behavior/UI change: the drawer still receives every piece of state
// and every callback from AdminDashboard exactly as before — the
// contract is now spelled out as a prop list so the parent file no
// longer has to carry the drawer's JSX. All existing data-testid,
// class names, order of blocks, copy, permissions and API calls are
// preserved verbatim.

import React, { useState } from "react";
import { toast } from "sonner";
import {
  X, Phone, Copy, Lock, CheckCircle2, MapPin, ExternalLink,
  AlertCircle, Link2, Link2Off, Calendar, Loader2, Clock,
} from "lucide-react";
import api, { formatError } from "@/lib/api";
import ChargeAdjustmentDialog from "./ChargeAdjustmentDialog";
import {
  STATUSES, STATUS_COLORS,
  copyMobileDigits, rowChargeLabel, statusChangeLine,
} from "./utils";
import { NotificationBadges, Info, MobileVerificationBadge } from "./components";

const ADMIN_NOTE_MAX_LENGTH = 1000;

export default function DetailDrawer({
  detail,
  onClose,
  onDetailChange,
  onRowPatch,
  isBridal,
  isAdmin = false,
  notes,
  noteText,
  setNoteText,
  noteBusy,
  onAddNote,
  onUpdateStatus,
  onToggleChargeable,
  onUnlinkStart,
}) {
  const [adjustOpen, setAdjustOpen] = useState(false);
  if (!detail) return null;

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/30" onClick={onClose}>
      <div
        className="w-full max-w-lg h-full bg-white overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="detail-drawer"
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-line">
          <div>
            <p className="overline">Submission</p>
            <h3 className="font-serif text-2xl text-ink">{detail.invoice_number}</h3>
          </div>
          <button onClick={onClose} className="text-muted2 hover:text-maroon" data-testid="close-drawer">
            <X size={20} strokeWidth={1.5} />
          </button>
        </div>
        <div className="p-6 space-y-5">
          <NotificationBadges submissionId={detail.id} />
          <Info label="Customer" value={detail.customer_name} />
          <div>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-muted2 mb-1.5">
              <Phone size={12} strokeWidth={1.7} />
              <span>Mobile</span>
            </div>
            <div className="flex items-center gap-2">
              <p data-testid="drawer-mobile" className="text-sm text-ink tabular-nums">+91-{detail.mobile}</p>
              <button
                type="button"
                data-testid={`drawer-copy-mobile-${detail.id}`}
                onClick={(e) => { e.stopPropagation(); copyMobileDigits(detail); }}
                className="text-muted2 hover:text-maroon transition-colors p-1.5 -m-1 inline-flex items-center justify-center rounded-full"
                title="Copy 10-digit mobile number"
                aria-label="Copy mobile number"
              >
                <Copy size={14} strokeWidth={1.7} />
              </button>
              <MobileVerificationBadge status={detail.mobile_verification} testId="drawer-mobile-verification" />
            </div>
            {detail.mobile_verification === "not_verified" && (
              <p
                data-testid="drawer-unverified-notice"
                className="mt-2 rounded-xl border border-[#FDE68A] bg-[#FFFBEB] px-3 py-2 text-xs text-[#92400E]"
              >
                Verification skipped — confirm this number by phone before dispatch.
              </p>
            )}
          </div>
          {isBridal && detail.invoice_number && /^(\d{3}|2\d{3})$/.test(detail.invoice_number) && (
            <Info label="Salesman" value={detail.salesman || "Not Assigned"} />
          )}
          {(isBridal || isAdmin) && detail.invoice_number && /^(\d{3}|2\d{3})$/.test(detail.invoice_number) && (
            <div>
              <p className="overline text-[10px] mb-2">Home Delivery Charge</p>
              <div className="flex flex-wrap items-center gap-2">
                {detail.home_delivery_chargeable ? (
                  <span
                    data-testid="drawer-chargeable-badge"
                    className="inline-flex items-center gap-1.5 rounded-full bg-maroon/[0.08] text-maroon text-xs font-semibold px-3 py-1.5 border border-maroon/25"
                  >
                    <Lock size={12} strokeWidth={1.8} /> {rowChargeLabel(detail)} · Chargeable
                  </span>
                ) : (
                  <span
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#EDF6EA] text-[#2D4A22] text-xs font-medium px-3 py-1.5 border border-[#2D4A22]/25"
                  >
                    Free Home Delivery
                  </span>
                )}
                {detail.payment?.status === "paid" && (
                  <span
                    data-testid="drawer-paid-badge"
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#D6E9D5] text-[#2D4A22] text-xs font-medium px-3 py-1.5 border border-[#2D4A22]/25"
                  >
                    <CheckCircle2 size={12} strokeWidth={1.8} /> ₹{Math.round((detail.payment.amount || 0) / 100)} Paid
                    {detail.payment.refund ? " · Refunded" : detail.charge_waived ? " · Waived" : " · Locked"}
                  </span>
                )}
                {detail.payment?.status === "paid" ? (
                  isAdmin && !detail.payment.refund && (
                    <button
                      type="button"
                      data-testid="drawer-charge-adjust-btn"
                      onClick={() => setAdjustOpen(true)}
                      className="text-xs px-3 py-1.5 rounded-full border border-maroon/40 text-maroon hover:bg-maroon/[0.06] transition-colors"
                    >
                      Exceptional adjustment…
                    </button>
                  )
                ) : (
                <button
                  type="button"
                  data-testid="drawer-toggle-chargeable"
                  onClick={() => onToggleChargeable(detail, !detail.home_delivery_chargeable)}
                  className="text-xs px-3 py-1.5 rounded-full border border-line hover:border-maroon hover:text-maroon transition-colors"
                >
                  {detail.home_delivery_chargeable ? "Make Home Delivery Free" : "Charge for Home Delivery"}
                </button>
                )}
              </div>
              {detail.payment?.status === "paid" && (
                <p data-testid="drawer-charge-locked-note" className="text-[11px] text-[#2D4A22] mt-1.5 leading-relaxed">
                  Charge locked — the customer paid ₹{Math.round((detail.payment.amount || 0) / 100)} online
                  {detail.payment.paid_at ? ` on ${new Date(detail.payment.paid_at).toLocaleString("en-IN")}` : ""}. It cannot be changed to free by staff.
                </p>
              )}
              {(detail.charge_adjustments || []).map((a) => (
                <p key={a.id} data-testid="drawer-charge-adjustment" className="text-[11px] text-muted2 mt-1">
                  {a.kind === "waiver" ? "Waiver" : "Refund"} recorded {new Date(a.at).toLocaleString("en-IN")} by {a.by} · ₹{Math.round((a.original_amount_paise || 0) / 100)} → ₹0 · "{a.reason}"
                </p>
              ))}
              <p className="text-[11px] text-muted2 mt-1.5 leading-relaxed">
                Applies to Home Delivery only for this invoice. In-Store Pickup is always free.
                Customer pays securely via Razorpay at submission.
              </p>
              {adjustOpen && (
                <ChargeAdjustmentDialog
                  detail={detail}
                  onClose={() => setAdjustOpen(false)}
                  onApplied={(patch) => { onDetailChange({ ...detail, ...patch }); onRowPatch(detail.id, patch); }}
                />
              )}
            </div>
          )}
          <Info label="Submission ID" value={detail.id} />
          <Info label="Submitted" value={new Date(detail.submitted_at).toLocaleString("en-IN")} icon={Calendar} />

          {/* Linked companion invoices (cross-group) — appears only when a link exists. */}
          {Array.isArray(detail.linked_invoices) && detail.linked_invoices.length > 0 && (
            <div data-testid="drawer-linked-block">
              <p className="overline text-[10px] mb-2">Linked Invoices</p>
              <div className="flex flex-col gap-2">
                {detail.linked_invoices.map((l) => (
                  <div
                    key={l.invoice_number}
                    data-testid={`drawer-linked-${l.invoice_number}`}
                    className="flex items-center justify-between gap-3 rounded-xl border border-[#B79E75]/40 bg-[#EFEAE1]/60 px-3 py-2.5"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Link2 size={14} strokeWidth={1.9} className="text-[#8A6E43] flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm text-ink font-medium truncate">
                          Invoice {l.invoice_number}
                        </p>
                        <p className="text-[11px] text-muted2">
                          {l.group_label} group · linked companion order
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      data-testid={`drawer-unlink-${l.invoice_number}`}
                      onClick={() => onUnlinkStart({ row: detail, link: l })}
                      className="text-[11px] text-[#8A6E43] hover:text-maroon inline-flex items-center gap-1 transition-colors"
                      title={`Unlink invoice ${l.invoice_number}`}
                    >
                      <Link2Off size={12} strokeWidth={1.8} /> Unlink
                    </button>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-muted2 mt-1.5 leading-relaxed">
                Companion invoice from the other side of the business. Payments,
                delivery, and dupatta status remain independent on both invoices.
              </p>
            </div>
          )}

          <div>
            <p className="overline text-[10px] mb-1.5">Delivery Address</p>
            <p className="text-sm text-ink leading-relaxed">
              {detail.flat_wing_floor && <>{detail.flat_wing_floor}<br /></>}
              {detail.building_society && <>{detail.building_society}<br /></>}
              {detail.address?.formatted_address || [detail.address?.street, detail.address?.city, detail.address?.state, detail.address?.pincode].filter(Boolean).join(", ")}
            </p>
            {detail.address?.maps_url && /^https?:\/\//i.test(String(detail.address.maps_url)) && (
              <a
                href={detail.address.maps_url}
                target="_blank"
                rel="noreferrer noopener"
                className="mt-2 inline-flex items-center gap-1.5 text-xs text-maroon hover:underline"
                data-testid="drawer-maps-link"
              >
                <MapPin size={13} strokeWidth={1.6} /> Open in Google Maps
                <ExternalLink size={11} strokeWidth={1.6} />
              </a>
            )}
          </div>
          {detail.landmark && <Info label="Landmark" value={detail.landmark} />}
          {detail.fulfillment_type === "home_delivery" && (
            <div>
              <p className="overline text-[10px] mb-2">Dupatta</p>
              <div className="flex flex-wrap items-center gap-2">
                {detail.dupatta_status === "collected" ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-[#D6E9D5] text-[#2D4A22] text-xs font-medium px-3 py-1.5 border border-[#2D4A22]/25">
                    <CheckCircle2 size={13} strokeWidth={1.8} /> Dupatta Collected
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F5D3D3] text-[#8A2A2A] text-xs font-medium px-3 py-1.5 border border-[#8A2A2A]/25">
                    <AlertCircle size={13} strokeWidth={1.8} /> Dupatta Pending
                  </span>
                )}
                <button
                  type="button"
                  data-testid="drawer-toggle-dupatta"
                  onClick={async () => {
                    const next = detail.dupatta_status === "collected" ? "include" : "collected";
                    try {
                      await api.patch(`/admin/submissions/${detail.id}/dupatta`, { status: next });
                      onDetailChange({ ...detail, dupatta_status: next });
                      onRowPatch(detail.id, { dupatta_status: next });
                      toast.success(next === "collected" ? "Marked as Dupatta Collected" : "Marked as Dupatta Pending");
                    } catch (e) {
                      toast.error(formatError(e));
                    }
                  }}
                  className="text-xs px-3 py-1.5 rounded-full border border-line hover:border-maroon hover:text-maroon transition-colors"
                >
                  {detail.dupatta_status === "collected" ? "Mark as Pending" : "Mark Dupatta Collected"}
                </button>
              </div>
            </div>
          )}
          <Info label="Additional Customisation" value={detail.additional_customisation === "yes" ? "Yes" : "No"} />
          {detail.additional_customisation === "yes" && detail.customisation_details && (
            <div>
              <p className="overline text-[10px] mb-1.5">Customisation Details</p>
              <p className="text-sm text-ink leading-relaxed whitespace-pre-wrap">{detail.customisation_details}</p>
            </div>
          )}
          {detail.special_instructions && (
            <div>
              <p className="overline text-[10px] mb-1.5">Special Instructions</p>
              <p className="text-sm text-ink leading-relaxed whitespace-pre-wrap">{detail.special_instructions}</p>
            </div>
          )}
          <div>
            <p className="overline text-[10px] mb-2">Status</p>
            <select
              value={detail.status}
              onChange={async (e) => { const next = e.target.value; const prev = detail.status; onDetailChange({ ...detail, status: next }); const ok = await onUpdateStatus(detail.id, next); if (ok === false) onDetailChange({ ...detail, status: prev }); }}
              className={`text-xs px-3 py-2 rounded-full border-0 font-medium cursor-pointer focus:outline-none focus:ring-2 focus:ring-maroon/30 ${STATUS_COLORS[detail.status] || "bg-gray-100"}`}
              data-testid="drawer-status-select"
            >
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            {statusChangeLine(detail) && (
              <div
                className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-muted2 tabular-nums"
                data-testid="drawer-status-ts"
              >
                <Clock size={11} strokeWidth={1.7} />
                <span>{statusChangeLine(detail)}</span>
              </div>
            )}
          </div>

          {/* Internal Notes */}
          <div className="pt-4 border-t border-line" data-testid="notes-section">
            <p className="overline text-[10px] mb-3">Internal Notes</p>
            <div className="flex gap-2 mb-4">
              <textarea
                data-testid="note-input"
                className="luxury-input !py-2.5 flex-1 min-h-[64px] text-sm"
                placeholder="Add a note for packing or courier follow-up…"
                value={noteText}
                maxLength={ADMIN_NOTE_MAX_LENGTH}
                onChange={(e) => setNoteText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) onAddNote(); }}
              />
              <button
                data-testid="add-note-btn"
                onClick={onAddNote}
                disabled={noteBusy || !noteText.trim()}
                className="btn-maroon !px-4 !py-2 !text-xs self-start whitespace-nowrap"
              >
                {noteBusy ? <Loader2 className="animate-spin" size={14} /> : "Add"}
              </button>
            </div>
            <p className="-mt-3 mb-4 text-right text-[10px] text-muted2" data-testid="note-length-count">
              {noteText.length}/{ADMIN_NOTE_MAX_LENGTH}
            </p>

            {notes.length === 0 ? (
              <p className="text-xs text-muted2 italic" data-testid="notes-empty">
                No notes yet. Add packing quirks, courier remarks, or reminders.
              </p>
            ) : (
              <ol className="relative border-l border-line pl-4 space-y-4" data-testid="notes-list">
                {notes.map((n) => (
                  <li key={n.id} className="relative">
                    <span className="absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full bg-maroon border-2 border-white shadow" />
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="text-[11px] font-medium text-ink">{n.author}</span>
                      <span className="text-[10px] uppercase tracking-[0.18em] text-muted2 whitespace-nowrap">
                        {new Date(n.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </div>
                    <p className="text-sm text-ink mt-1 leading-relaxed whitespace-pre-wrap">{n.text}</p>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
